import {
	Box,
	Divider,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { PlusIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AddGoalCta } from "web_myplann/Goals/Components/AddGoalCta";
import { CompletedGoalListItem } from "web_myplann/Goals/Components/CompletedGoalListItem";
import { GoalListItem } from "web_myplann/Goals/Components/GoalListItem";
import { GoalsEmptyState } from "web_myplann/Goals/Components/GoalsEmptyState";
import {
	DUMMY_ACTIVE_GOALS,
	DUMMY_COMPLETED_GOALS,
} from "web_myplann/Goals/DummyData/goalsDummyData";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";

export const GoalsList = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const activeGoals = DUMMY_ACTIVE_GOALS;
	const completedGoals = DUMMY_COMPLETED_GOALS;

	const isEmpty = activeGoals.length === 0 && completedGoals.length === 0;

	const handleGoalTap = (goalId: string) => {
		navigate(Routing.Goals.GoalFormEdit.path(goalId));
	};

	const handleAddGoal = () => {
		navigate(getRoute(Routing.Goals.GoalFormCreate));
	};

	const ctaTitle =
		activeGoals.length === 0 && completedGoals.length > 0
			? t(Resources.Goals.List.ctaTitleCompleted)
			: t(Resources.Goals.List.ctaTitle);

	if (isEmpty) {
		return (
			<MyPlannLayout
				disableContentPadding
				disableTopPadding
				topBarBgColor="transparent"
				overlayTopBar
				topBar={
					<NavigationHeader
						title={
							<Typography
								fontWeight={700}
								fontSize={16}
								color={theme.palette.common.white}
							>
								{t(Resources.Goals.List.title)}
							</Typography>
						}
						bgColor="transparent"
					/>
				}
			>
				<GoalsEmptyState />
			</MyPlannLayout>
		);
	}

	if (isEmpty) {
		return (
			<MyPlannLayout
				bgColor={theme.palette.grey[100]}
				disableContentPadding
				disableTopPadding
				topBarBgColor="transparent"
				topBar={
					<NavigationHeader
						title={
							<Typography
								fontWeight={700}
								fontSize={16}
								color={theme.palette.common.black}
							>
								{t(Resources.Goals.List.title)}
							</Typography>
						}
						bgColor={theme.palette.common.white}
					/>
				}
			>
				<GoalsEmptyState />
			</MyPlannLayout>
		);
	}

	return (
		<MyPlannLayout
			bgColor={theme.palette.grey[50]}
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Goals.List.title)}
					endComponent={
						<IconButton onClick={handleAddGoal}>
							<PlusIcon size={16} color={theme.palette.grey[900]} />
						</IconButton>
					}
					bgColor={theme.palette.common.white}
				/>
			}
		>
			<NativePullToRefresh>
				<Stack pt={1}>
					<CollapsibleSection title={t(Resources.Goals.List.activeGoals)}>
						<Stack bgcolor={theme.palette.background.paper}>
							{activeGoals.map((goal, i) => (
								<Box key={goal.id}>
									<GoalListItem goal={goal} onClick={handleGoalTap} />
									{i < activeGoals.length - 1 && (
										<Divider
											sx={{
												borderColor: theme.palette.grey[100],
											}}
										/>
									)}
								</Box>
							))}
						</Stack>
					</CollapsibleSection>

					{completedGoals.length > 0 && (
						<Stack pt={1.2}>
							<CollapsibleSection
								title={`${t(Resources.Goals.List.completed)} (${completedGoals.length})`}
								expandable
							>
								<Stack bgcolor={theme.palette.background.paper}>
									{completedGoals.map((goal, i) => (
										<Box key={goal.id}>
											<CompletedGoalListItem
												goal={goal}
												onClick={handleGoalTap}
											/>
											{i < completedGoals.length - 1 && (
												<Divider
													sx={{
														mx: 1.6,
														borderColor: theme.palette.grey[100],
													}}
												/>
											)}
										</Box>
									))}
								</Stack>
							</CollapsibleSection>
						</Stack>
					)}

					<AddGoalCta title={ctaTitle} onClick={handleAddGoal} />
				</Stack>
			</NativePullToRefresh>
		</MyPlannLayout>
	);
};
